import { useEffect, useState, useRef } from 'react';
import Spinner from 'components/general/Spinner';
import { Row, Col } from 'reactstrap';

import { useActions } from 'utils/hooks';
import SingleValue from './SingleValue';
import * as indexesActions from 'store/market-indexes/actions';
import { transformValues } from './helpers';
import moment from 'moment';
import { strToNumber } from 'common/utils/numbers';

const EditValuesForm = ({ isOpen, active, drawer, refetchData, setDeleting }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState([]);
  const [meta, setMeta] = useState({});
  const [isFetching, setisFetching] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const scrollRef = useRef(null);

  const [newDate, setNewDate] = useState(moment().format('YYYY-MM-DD'));
  const [newValue, setNewValue] = useState(null);

  const [dateError, setDateError] = useState(null);
  const [valueError, setValueError] = useState(null);

  const [activeEdit, setActiveEdit] = useState(null);
  //   const [editValue, setEditValue] = useState(null);
  //   const [editDate, setEditDate] = useState(null);

  const [getIndexValues, updateIndexValue, deleteIndexValue] = useActions([
    indexesActions.getIndexValues,
    indexesActions.updateIndexValue,
    indexesActions.deleteIndexValue
  ]);

  useEffect(() => {
    setNewDate(moment().format('YYYY-MM-DD'));
    if (drawer.isOpen) {
      setActiveEdit(null);
    }
  }, [drawer.isOpen]);

  const load = async () => {
    setLoading(true);
    const data = await getIndexValues({ id: active.id, per_page: 30, current_page: 1 });
    setMeta(data.meta);
    setValues(transformValues(data.data));

    setLoading(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    load();
  }, [isOpen]);

  const fetchIndexValues = async (currentPage = 1, shouldConcatValues) => {
    try {
      setisFetching(true);
      const data = await getIndexValues({ id: active.id, per_page: 30, current_page: currentPage });
      setValues(
        shouldConcatValues ? values.concat(transformValues(data.data)) : transformValues(data.data)
      );
      setMeta(data.meta);
      setisFetching(false);
    } catch (err) {
      setisFetching(false);
      console.error(err);
    }
  };

  const addNewValue = async () => {
    if (!newDate) {
      setDateError('Please provide a date');
      return;
    }

    if (!newValue) {
      setValueError('Please provide a value');
      return;
    }

    const params = {
      date: moment(newDate).format('YYYY-MM-DD'),
      index: [
        {
          market_index_id: active.id,
          value: strToNumber(newValue)
        }
      ]
    };
    await updateIndexValue(params);
    setNewValue(null);
    fetchIndexValues();
  };

  const saveValue = async () => {
    if (!activeEdit.value_date) {
      setDateError('Please provide a date');
      return;
    }

    if (!activeEdit.value) {
      setValueError('Please provide a value');
      return;
    }

    const params = {
      date: moment(activeEdit.value_date).format('YYYY-MM-DD'),
      index: [
        {
          market_index_id: active.id,
          value: strToNumber(activeEdit.value)
        }
      ]
    };
    await updateIndexValue(params);

    fetchIndexValues();
    scrollRef.current?.scrollTo(0, 0);
    setActiveEdit(null);
  };

  const deleteValue = async () => {
    if (!activeEdit) return;
    setDisabled(true);
    const params = {
      date: moment(activeEdit.value_date).format('YYYY-MM-DD'),
      market_index_ids: [active.id]
    };

    deleteIndexValue(params).then(response => {
      if (response) {
        fetchIndexValues();
        setDisabled(false);
        setActiveEdit(null);
        setDeleting(false);
        scrollRef.current?.scrollTo(0, 0);
      }
    });
  };

  const onEditDateChange = date => {
    setActiveEdit({
      ...activeEdit,
      value_date: date
    });
  };

  const onEditInputChange = value => {
    setActiveEdit({
      ...activeEdit,
      value: value
    });
  };

  const loadMore = async () => {
    if (meta.current_page < meta.last_page) {
      fetchIndexValues(meta.current_page + 1, true);
    }
  };

  const onScroll = e => {
    if (isFetching) return;
    const paddingOffset = 1;

    const isBottom =
      e.target.scrollHeight - e.target.scrollTop - paddingOffset === e.target.clientHeight;

    if (isBottom) {
      loadMore();
    }
  };

  return loading ? (
    <Spinner className="ms-3 mt-2" />
  ) : (
    <div
      onScroll={onScroll}
      ref={scrollRef}
      className="edit-single-index-values dark-blue-scrollbar  pt-3 ps-4 pe-7 pb-4"
    >
      <Row>
        <Col className="">
          <Row className="mb-2">
            <Col xs={4}>
              <div className="text-primary fw-bold fs-12">DATE</div>
            </Col>
            <Col xs={6}>
              <div className="index-name text-primary fw-bold fs-12 text-uppercase">
                {active.name}
              </div>
            </Col>
          </Row>
          <SingleValue
            dateError={!activeEdit ? dateError : ''}
            valueError={!activeEdit ? valueError : ''}
            value={newValue}
            date={newDate}
            onDateChange={date => {
              setDateError(null);
              setNewDate(date);
            }}
            onNumberInputChange={value => {
              setValueError(null);
              setNewValue(value);
            }}
            onAddClick={addNewValue}
            mode="add"
            className={activeEdit ? 'disabled' : ''}
          />
          {values.map(indexValue => {
            return (
              <SingleValue
                dateError={activeEdit && activeEdit.id === indexValue.id ? dateError : null}
                onSaveClick={saveValue}
                onDeleteClick={deleteValue}
                valueError={activeEdit && activeEdit.id === indexValue.id ? valueError : null}
                indexValue={indexValue}
                value={
                  activeEdit && activeEdit.id === indexValue.id
                    ? activeEdit.value
                    : indexValue.value
                }
                date={activeEdit ? activeEdit.value_date : indexValue.value_date}
                key={indexValue.id}
                activeEdit={activeEdit}
                setActiveEdit={setActiveEdit}
                onEditClick={() => setActiveEdit(indexValue)}
                mode={
                  !activeEdit ? 'display' : activeEdit.id === indexValue.id ? 'edit' : 'display'
                }
                onDateChange={date => {
                  setDateError(null);
                  onEditDateChange(date);
                }}
                onNumberInputChange={value => {
                  setValueError(null);
                  onEditInputChange(value);
                }}
                disabled={disabled}
                setDeleting={setDeleting}
              />
            );
          })}
        </Col>
      </Row>
      {isFetching && (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner className="ms-3 mt-2" />
        </div>
      )}
    </div>
  );
};

export default EditValuesForm;
